import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MaintenanceIssuesService } from '../services/issues.service';
import type { 
  MaintenanceIssue, 
  IssueFilters 
} from '../types/maintenance-issues.types';
import { useState } from 'react';

export function useIssues(companyId: string, initialFilters: IssueFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<IssueFilters>({
    status: 'all',
    severity: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['maintenance-issues', companyId, filters],
    queryFn: () => MaintenanceIssuesService.getIssues(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['maintenance-issues-stats', companyId],
    queryFn: () => MaintenanceIssuesService.getIssueStats(companyId),
    enabled: !!companyId,
  });

  const createIssueMutation = useMutation({
    mutationFn: (data: Partial<MaintenanceIssue>) => MaintenanceIssuesService.createIssue(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-issues'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-issues-stats'] });
    },
  });

  const updateIssueMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string, updates: Partial<MaintenanceIssue> }) => 
      MaintenanceIssuesService.updateIssue(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-issues'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-issues', 'detail', variables.id] });
    },
  });

  const assignIssueMutation = useMutation({
    mutationFn: ({ id, userId, teamId }: { id: string, userId: string, teamId?: string }) => 
      MaintenanceIssuesService.assignIssue(id, userId, teamId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-issues'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-issues', 'detail', variables.id] });
    },
  });

  return {
    ...query,
    issues: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoadingStats: statsQuery.isLoading,
    filters,
    setFilters,
    createIssue: createIssueMutation.mutateAsync,
    updateIssue: updateIssueMutation.mutateAsync,
    assignIssue: assignIssueMutation.mutateAsync,
    isCreating: createIssueMutation.isPending,
    isUpdating: updateIssueMutation.isPending,
    isAssigning: assignIssueMutation.isPending,
  };
}

export function useIssueDetail(id?: string) {
  return useQuery({
    queryKey: ['maintenance-issues', 'detail', id],
    queryFn: () => MaintenanceIssuesService.getIssueDetail(id!),
    enabled: !!id,
  });
}
